import {
  AlertTriangle,
  CalendarRange,
  History,
  Info,
  Trash2Icon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { IntegrationToken } from '@/features/integrations/api/get-integration-tokens';
import { Button } from '@/shared/components/ui/button';
import { formatDate, formatRelativeTime } from '@/shared/utils/format';

interface IntegrationTokenCardProps {
  token: IntegrationToken;
  onDelete?: (token: IntegrationToken) => void;
}

export const IntegrationTokenCard = ({
  token,
  onDelete,
}: IntegrationTokenCardProps) => {
  const { t } = useTranslation('integrations');
  const { t: tCommon } = useTranslation('common');

  const isTokenExpired =
    token?.expires_at && new Date(token.expires_at) < new Date();

  return (
    <div
      className={`flex flex-row items-center justify-between rounded-lg bg-light-container-alt p-4 ${isTokenExpired ? 'opacity-60' : ''}`}
    >
      <div className="flex flex-1 flex-col gap-2">
        <div className="font-semibold">{token.title}</div>
        <div className="flex flex-row flex-wrap gap-4 text-sm text-white/60">
          <div className="flex items-center gap-2">
            <CalendarRange className="size-4" />
            {formatDate({
              date: new Date(token.created_at),
              formatType: 'digits',
            })}
          </div>
          {!isTokenExpired && token.last_used_at && (
            <>
              <div className="h-4 w-px bg-white/10" />
              <div className="flex items-center gap-2">
                <History className="size-4" />
                {formatRelativeTime({
                  date: new Date(token.last_used_at),
                  t: tCommon,
                })}
              </div>
            </>
          )}
        </div>
        <div className="flex flex-row flex-wrap gap-4 text-sm text-white/60">
          {token.expires_at && (
            <div className="flex items-center gap-2">
              {isTokenExpired ? (
                <>
                  <Info className="size-4 text-red-500" />
                  {t('IntegrationsTab.expired_at')}{' '}
                  {formatDate({
                    date: new Date(token.expires_at),
                    formatType: 'digits',
                  })}
                </>
              ) : (
                <>
                  <AlertTriangle className="size-4 text-orange-400" />
                  {t('IntegrationsTab.expires_on')}{' '}
                  {formatDate({
                    date: new Date(token.expires_at),
                    formatType: 'digits',
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {onDelete && (
        <div className="ml-4 flex items-center gap-2">
          <Button
            variant="outline"
            btnType="icon"
            onClick={() => onDelete(token)}
          >
            <Trash2Icon className="size-4 text-error-red" />
          </Button>
        </div>
      )}
    </div>
  );
};
